import type { FieldsDataStructure } from "../types/keyword.types";

// ============================================================================
// STEPPER UTILITY FUNCTIONS
// ============================================================================

// ============================================================================
// KEYWORD MANAGEMENT
// ============================================================================
export function generateKeywordsQuery(
  mainKeywords: string[],
  additionalKeywords: string[],
  excludedKeywords: string[],
): string {
  const parts: string[] = [];
  if (mainKeywords.length > 0) {
    const mainPart = mainKeywords.map((k) => `"${k}"`).join(" OR ");
    parts.push(`( ${mainPart} )`);
  }
  if (additionalKeywords.length > 0) {
    if (parts.length > 0) parts.push("AND");
    const additionalPart = additionalKeywords.map((k) => `"${k}"`).join(" OR ");
    parts.push(`( ${additionalPart} )`);
  }
  if (excludedKeywords.length > 0) {
    if (parts.length > 0) parts.push("AND NOT");
    const excludedPart = excludedKeywords.map((k) => `"${k}"`).join(" OR ");
    parts.push(`( ${excludedPart} )`);
  }
  return parts.join(" ");
}

export function addKeyword(
  keyword: string,
  mainKeywords: string[],
  additionalKeywords: string[],
  excludedKeywords: string[],
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  inputSetter: React.Dispatch<React.SetStateAction<string>>,
) {
  const trimmedKeyword = keyword.trim();
  if (
    trimmedKeyword &&
    !mainKeywords.includes(trimmedKeyword) &&
    !additionalKeywords.includes(trimmedKeyword) &&
    !excludedKeywords.includes(trimmedKeyword)
  ) {
    setter((prev) => [...prev, trimmedKeyword]);
    inputSetter("");
  }
}

export function removeKeyword(
  keyword: string,
  setter: React.Dispatch<React.SetStateAction<string[]>>,
) {
  setter((prev) => prev.filter((k) => k !== keyword));
}

export function handleKeyPress(
  e: React.KeyboardEvent,
  inputValue: string,
  mainKeywords: string[],
  additionalKeywords: string[],
  excludedKeywords: string[],
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  inputSetter: React.Dispatch<React.SetStateAction<string>>,
) {
  if (e.key === "Enter" || e.key === ",") {
    e.preventDefault();
    addKeyword(
      inputValue,
      mainKeywords,
      additionalKeywords,
      excludedKeywords,
      setter,
      inputSetter,
    );
  }
}

export function handleClearAll(
  setMainKeywords: React.Dispatch<React.SetStateAction<string[]>>,
  setAdditionalKeywords: React.Dispatch<React.SetStateAction<string[]>>,
  setExcludedKeywords: React.Dispatch<React.SetStateAction<string[]>>,
  setMainInput: React.Dispatch<React.SetStateAction<string>>,
  setAdditionalInput: React.Dispatch<React.SetStateAction<string>>,
  setExcludedInput: React.Dispatch<React.SetStateAction<string>>,
) {
  setMainKeywords([]);
  setAdditionalKeywords([]);
  setExcludedKeywords([]);
  setMainInput("");
  setAdditionalInput("");
  setExcludedInput("");
}

// ============================================================================
// FIELD MANAGEMENT
// ============================================================================
export function toggleField(
  field: string,
  setExpandedFields: React.Dispatch<React.SetStateAction<string[]>>,
) {
  setExpandedFields((prev) =>
    prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
  );
}

export function handleSubFieldChange(
  subField: string,
  checked: boolean,
  selectedSubFields: string[],
  setSelectedSubFields: React.Dispatch<React.SetStateAction<string[]>>,
) {
  if (checked) {
    setSelectedSubFields([...selectedSubFields, subField]);
  } else {
    setSelectedSubFields(selectedSubFields.filter((s) => s !== subField));
  }
}

export function handleSelectAll(
  filteredFieldsData: FieldsDataStructure,
  setExpandedFields: React.Dispatch<React.SetStateAction<string[]>>,
  setSelectedSubFields: React.Dispatch<React.SetStateAction<string[]>>,
) {
  const allSubFields = Object.values(filteredFieldsData).flatMap(
    (field) => field.subFields,
  );
  setExpandedFields(Object.keys(filteredFieldsData));
  setSelectedSubFields(allSubFields);
}

export function handleClearAllFields(
  setSelectedSubFields: React.Dispatch<React.SetStateAction<string[]>>,
) {
  setSelectedSubFields([]);
}

export function isFieldSelected(
  field: string,
  filteredFieldsData: FieldsDataStructure,
  selectedSubFields: string[],
) {
  const subFields = filteredFieldsData[field]?.subFields || [];

  if (subFields.length > 0) {
    return subFields.every((subField: string) =>
      selectedSubFields.includes(subField),
    );
  }

  return selectedSubFields.includes(field);
}

export function isFieldIndeterminate(
  field: string,
  filteredFieldsData: FieldsDataStructure,
  selectedSubFields: string[],
) {
  const subFields = filteredFieldsData[field]?.subFields || [];

  if (subFields.length > 0) {
    const selectedCount = subFields.filter((subField: string) =>
      selectedSubFields.includes(subField),
    ).length;
    return selectedCount > 0 && selectedCount < subFields.length;
  }

  return false;
}

export function handleFieldChange(
  field: string,
  checked: boolean,
  filteredFieldsData: FieldsDataStructure,
  setSelectedSubFields: React.Dispatch<React.SetStateAction<string[]>>,
) {
  const subFields = filteredFieldsData[field]?.subFields || [];

  if (subFields.length > 0) {
    if (checked) {
      setSelectedSubFields((prev) => [...new Set([...prev, ...subFields])]);
    } else {
      setSelectedSubFields((prev) =>
        prev.filter((subField) => !subFields.includes(subField)),
      );
    }
  } else {
    if (checked) {
      setSelectedSubFields((prev) => [...new Set([...prev, field])]);
    } else {
      setSelectedSubFields((prev) =>
        prev.filter((subField) => subField !== field),
      );
    }
  }
}
